import Actions from "../actions";
import { LoggedInAction, SessionUserUpdatedAction } from "./types";
import { LoggedUser, MfaMethodEnum, MfaRequiredResponse } from "../../models";
import { AppThunkAction, ApplicationState } from "..";
import { authenticateAsync, authenticateWithGoogleAsync, getClientsReducedDataAsync, getProfessionalsReducedDataAsync, getProfessionalMeAsync, logoutAsync, verifyMfaAsync } from "../../ajax/requests";
import { TK } from "../translations/translationKeys";
import { AppAction, ErrorAction } from "../types";
import { alertDismiss, alertError, alertSuccess } from "../alerts/actions";
import { loadEntities } from "../entities/actions";
import { loadSettings } from "../settings/actions";
import { loadNotifications, selectedBranchChange, loadIncomingMessagesSummary, selectCashRegisterName } from "../general/actions";
import { canAccessClientsChat, canEditAppointments, canEditProfessionals, canSeeAppointments, canSeeClients, getMyUser } from "./selectors";
import { translate } from "../translations/hooks";
import { getSelectedBranch, getSelectedCashRegisterName } from "../general/selectors";
import Cookies from 'js-cookie';
import { saveAuthContext } from "../../shared/authContext";

// Helper to check if response is MfaRequiredResponse
export const isMfaRequired = (response: LoggedUser | MfaRequiredResponse): response is MfaRequiredResponse => {
    return (response as MfaRequiredResponse).mfaRequired === true;
};

export const loggedIn = (data: LoggedUser): LoggedInAction => ({
    type: Actions.LoggedIn,
    payload: data,
});

export const loggedOut = (): AppAction => ({
    type: Actions.LoggedOut,
});

export const updateSessionUser = (data: Partial<LoggedUser>): SessionUserUpdatedAction => ({
    type: Actions.SessionUserUpdated,
    payload: data,
});

const loggingIn = (): AppAction => ({
    type: Actions.LoggingIn,
});

const loginFailed = (message: string): ErrorAction => ({
    type: Actions.LoginFailed,
    payload: message,
});

const getMessageFromLoginException = (error: any, state: ApplicationState): string => {
    const status = error?.status ?? error?.response?.status;
    if (status === 400) {
        return translate(TK.wrongUsernameOrPassword);
    }
    return translate(TK.somethingWrongWithYourLogin);
}

export const loadBaseData = (): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState()
        const user = getMyUser(state)
        const selectedBranch = getSelectedBranch(state)

        if (user && !user.isClient) {
            if(user.professionalId) {
                dispatch(loadEntities('professionals', getProfessionalMeAsync, 'AppendResult'))
            }
            
            dispatch(loadNotifications())

            if (canAccessClientsChat(state)) {
                dispatch(loadIncomingMessagesSummary())
            }
            
            dispatch(loadEntities('clients', getClientsReducedDataAsync, 'ReducedDataLoad'))

            dispatch(loadEntities('professionals', getProfessionalsReducedDataAsync, 'ReducedDataLoad'))
            
            dispatch(loadSettings()).then(settings => {
                if (!selectedBranch || !(settings.general?.branches?.[selectedBranch])) {
                    const prevBranch = Cookies.get('SELECTED_BRANCH')
                    if (prevBranch && settings.general?.branches?.[prevBranch]) {
                        dispatch(selectedBranchChange(prevBranch))
                    } else {
                        const branch = Object.keys(settings.general?.branches ?? {})[0]
                        if (branch) dispatch(selectedBranchChange(branch))
                    }
                }

                const cashSettings = settings.cashRegisterSettings;
                if (cashSettings?.enabled && cashSettings?.sharedRegisterNames?.length > 0) {
                    const currentRegister = getSelectedCashRegisterName(getState());
                    if (currentRegister === undefined && user?.defaultCashRegisterName !== undefined) {
                        dispatch(selectCashRegisterName(user.defaultCashRegisterName));
                    }
                }
            })
        }
    }
} 

export const internetLost = (): AppThunkAction<void> => {
    return (dispatch, getState) => {
        const state = getState()
        if (!state.alerts.items.some(({id}) => id === 'no-internet')) dispatch(alertError(translate(TK.noInternetMessage), 120000, 'no-internet'))
    }
}

export const internetOk = (): AppThunkAction<void> => {
    return (dispatch, getState) => {
        const state = getState()
        if (state.alerts.items.some(({id}) => id === 'no-internet')) dispatch(alertDismiss('no-internet'))
    }
}

export const login = (username: string, password: string, isClient: boolean, companyId?: string, impersonateUsername?: string) : AppThunkAction<Promise<LoggedUser | MfaRequiredResponse | null>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            dispatch(loggedOut())
            dispatch(loggingIn())

            const response = await authenticateAsync(username, password, isClient, companyId, impersonateUsername);

            // Check if MFA is required
            if (isMfaRequired(response)) {
                // Reset loading state - MFA modal will handle verification
                dispatch(loginFailed(''));
                return response;
            }

            const user = response as LoggedUser;

            // Strip token from user object (auth is cookie-based)
            const { token, ...userWithoutToken } = user;

            // For clients with no businesses, return user without completing login
            if (isClient && (!user?.businesses || user.businesses.length === 0)) {
                return userWithoutToken as LoggedUser;
            }

            // For professionals with multiple businesses, return user for business selection
            if (!isClient && user?.businesses && user.businesses.length > 1) {
                return userWithoutToken as LoggedUser;
            }

            // Save auth context on successful login
            saveAuthContext(isClient ? 'client' : 'professional');

            dispatch(loggedIn(userWithoutToken as LoggedUser))
            dispatch(loadBaseData())
            dispatch(alertSuccess(translate(TK.loggedIn)))
            return userWithoutToken as LoggedUser;
        }
        catch(error) {
            const status = (error as any)?.status ?? (error as any)?.response?.status;
            if (status === 403) return null // Forbid is globally handled
            const msg = getMessageFromLoginException(error, getState());
            dispatch(loginFailed(msg));
            dispatch(alertError(msg));
            return null;
        }
    };
};

export const verifyMfa = (sessionId: string, otp: string, isClient: boolean, mfaMethod?: MfaMethodEnum): AppThunkAction<Promise<LoggedUser | null>> => {
    return async (dispatch, getState) => {
        try {
            dispatch(loggingIn());

            const user = await verifyMfaAsync(sessionId, otp, mfaMethod);

            // Strip token from user object (auth is cookie-based)
            const { token, ...userWithoutToken } = user;

            // For clients with no businesses, return user without completing login
            if (isClient && (!user?.businesses || user.businesses.length === 0)) {
                return userWithoutToken as LoggedUser;
            }

            // For professionals with multiple businesses, return user for business selection
            if (!isClient && user?.businesses && user.businesses.length > 1) {
                return userWithoutToken as LoggedUser;
            }

            // Save auth context on successful login
            saveAuthContext(isClient ? 'client' : 'professional');

            dispatch(loggedIn(userWithoutToken as LoggedUser));
            dispatch(loadBaseData());
            dispatch(alertSuccess(translate(TK.loggedIn)));
            return userWithoutToken as LoggedUser;
        } catch (error) {
            const status = (error as any)?.status ?? (error as any)?.response?.status;
            if (status === 403) return null;
            const msg = status === 400
                ? translate(TK.invalidOrExpiredCode)
                : getMessageFromLoginException(error, getState());
            dispatch(loginFailed(msg));
            dispatch(alertError(msg));
            return null;
        }
    };
};

export const loginWithGoogle = (idToken: string, isClient: boolean, companyId?: number) : AppThunkAction<Promise<LoggedUser | null>> => {
    return async (dispatch, getState) => {
        try {
            dispatch(loggedOut())
            dispatch(loggingIn())

            const user = await authenticateWithGoogleAsync(idToken, isClient, companyId);

            if (!user) {
                const msg = getMessageFromLoginException(null, getState());
                dispatch(loginFailed(msg));
                dispatch(alertError(msg));
                return null;
            }

            // Strip token from user object (auth is cookie-based)
            const { token, ...userWithoutToken } = user;

            // For onboarding or professionals with multiple businesses, return user without completing login
            if (user?.needsOnboarding || (!isClient && user?.businesses && user.businesses.length > 1)) {
                return userWithoutToken as LoggedUser;
            }

            // For clients with no businesses, return user without completing login
            if (isClient && (!user?.businesses || user.businesses.length === 0)) {
                return userWithoutToken as LoggedUser;
            }

            // Save auth context on successful login
            saveAuthContext(isClient ? 'client' : 'professional');

            dispatch(loggedIn(userWithoutToken as LoggedUser))
            dispatch(loadBaseData())
            dispatch(alertSuccess(translate(TK.loggedIn)))
            return userWithoutToken as LoggedUser;
        }
        catch(error) {
            const status = (error as any)?.status ?? (error as any)?.response?.status;
            if (status === 403) return null; // Forbid is globally handled

            // Handle 404 - User needs onboarding
            if (status === 404) {
                const responseData = (error as any)?.data ?? (error as any)?.response?.data;
                const needsOnboarding = responseData?.NeedsOnboarding ?? responseData?.needsOnboarding;
                if (needsOnboarding ?? true) {
                    return {
                        needsOnboarding: true,
                        email: responseData?.Email ?? responseData?.email,
                        username: responseData?.Username ?? responseData?.username,
                        name: responseData?.Name ?? responseData?.name,
                        photoUrl: responseData?.PhotoUrl ?? responseData?.photoUrl,
                    } as LoggedUser;
                }
            }

            const msg = getMessageFromLoginException(error, getState());
            dispatch(loginFailed(msg));
            dispatch(alertError(msg));
            return null;
        }
    };
};

export const clearSystemNotes = (): AppAction => ({
    type: Actions.SystemNotesCleared,
});

export const logout = () : AppThunkAction<void> => {
    return async (dispatch, getState) => {
        const user = getMyUser(getState())
        if (user) try { await logoutAsync(user.isClient ?? false) } catch { /* continue logout regardless */ }
        dispatch(loggedOut());
    };
};
